import i18n from 'i18next';

import showSnackbar from 'components/shared/Snackbar';

import { isPlanningCardSource } from './columnSlots';
import { wasReleased } from './dragRelease';

/**
 * Whether the card was let go over the board rather than away from it.
 *
 * Releasing over empty space is also how a drag is abandoned with the mouse, so
 * the message is worth showing only where the planner was plausibly aiming at
 * something: inside the board, on a column's dead space or a column that was
 * busy. Dropping a card off to the side reads as "never mind" and stays quiet.
 *
 * A rect comparison rather than `elementFromPoint`, which a column's
 * `LoadingOverlay` sits in front of and would answer for — the loading column is
 * exactly the case this has to get right.
 */
export const wasReleasedOnBoard = (
  board: HTMLElement | null | undefined,
  input: { clientX: number; clientY: number } | null | undefined
): boolean => {
  if (!board || !input) return false;
  const rect = board.getBoundingClientRect();
  return (
    input.clientX >= rect.left &&
    input.clientX <= rect.right &&
    input.clientY >= rect.top &&
    input.clientY <= rect.bottom
  );
};

/** Tells the planner that the card they just released was not planned. */
const notifyDropNotPlaced = () =>
  showSnackbar({
    severity: 'info',
    autoHideDuration: 3000,
    message: i18n.t('activityPlanning.dropNotPlaced')
  });

/**
 * Reports a card that was released without landing anywhere, and says whether
 * it did.
 *
 * Every view calls this once, first thing in its drop handler, so the whole
 * rule lives in one place: the drag has to have ended on no drop target, it has
 * to be a release rather than a cancel, on the board, carrying one of our
 * cards. Owning the drop-target check here is what lets the call sit at the top
 * rather than inside each view's "nothing was hit" branch.
 *
 * First thing, because taking `data-drag-active` off invalidates style across
 * the board and the layout read inside `wasReleasedOnBoard` would then have to
 * recalculate all of it. And unconditionally, because it also disarms the
 * release watch — every path needs that done exactly once.
 *
 * @returns whether the message was shown, for the caller's debug log.
 */
export const reportUnplacedRelease = (
  board: HTMLElement | null | undefined,
  location: {
    current: {
      input?: { clientX: number; clientY: number } | null;
      dropTargets: unknown[];
    };
  },
  sourceData: unknown
): boolean => {
  const released = wasReleased();
  const shouldReport =
    released &&
    location.current.dropTargets.length === 0 &&
    wasReleasedOnBoard(board, location.current.input) &&
    isPlanningCardSource(sourceData);

  if (shouldReport) notifyDropNotPlaced();
  return shouldReport;
};
